import styled from '@emotion/styled';
import { useState, forwardRef, SyntheticEvent, Fragment } from 'react';

import { ChakraTheme } from '@/styles/themes/default';
import { IThemeProps } from '@/styles/themes/types';

const InputElement = styled.textarea<{ isError: boolean; rows: number } & IThemeProps>`
  width: 100%;
  padding: 12px 16px;

  resize: vertical;
  line-height: 24px;
  min-height: calc(${({ rows }) => rows}*24px);
  background-color: transparent;
  border-radius: 4px;
  border: 1px solid
    ${({ isError, theme }) => (isError ? theme.legacy.colors.status.error : theme.legacy.colors.gray50)}};
  color: ${({ theme }) => theme.legacy.colors.black};

  &:focus {
    outline: none;
    border: 2px solid ${({ theme }) => theme.legacy.colors.brand.primary};
  }
`;

const labelColor = (isError: boolean, isFocus: boolean, theme: ChakraTheme) => {
  if (isFocus) {
    return theme.legacy.colors.brand.primary;
  }
  if (isError) {
    return theme.legacy.colors.status.error;
  }
  return theme.legacy.colors.lightText;
};

const InputLabel = styled('label')<{ isError: boolean; isFocused: boolean } & IThemeProps>`
  display: block;
  margin-bottom: 6px;

  color: ${({ theme, isError, isFocused }) => labelColor(isError, isFocused, theme)};
  font-size: 14px;
  line-height: 20px;
`;

interface ITextAreaProps {
  label: string;
  name: string;
  rows: number;
  isError?: boolean;
  onChange?: (event: SyntheticEvent) => void;
}

export const TextArea = forwardRef<undefined, ITextAreaProps>(
  ({ name, label, isError = false, onChange, rows }, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    return (
      <Fragment>
        <InputLabel htmlFor={name} isError={isError} isFocused={isFocused}>
          {label}
        </InputLabel>
        <InputElement
          isError={isError}
          ref={ref}
          id={name}
          name={name}
          rows={rows}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={(event) => onChange && onChange(event)}
        />
      </Fragment>
    );
  }
);
